import {
  Badge,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@paguz/ui';
import type { AdminApplicationDetail } from '../../api/origination-schemas.ts';
import { DataList, Panel } from '../../components/page.tsx';
import { formatDateTime, formatRate } from '../../lib/format.ts';

/** Central de riesgo (mock en el demo), KYC y listas, y scorecard del motor. */
export function RiskTab({ detail }: { detail: AdminApplicationDetail }) {
  const { bureau, kyc, scorecard } = detail.risk;
  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Central de riesgo"
        description={
          bureau
            ? `${bureau.provider} · consultada ${formatDateTime(bureau.consultedAt)}`
            : undefined
        }
      >
        {bureau ? (
          <div className="flex flex-col gap-5">
            <DataList
              columns={4}
              items={[
                {
                  label: 'Puntaje',
                  value: (
                    <span className="text-lg font-semibold">{bureau.score ?? 'Sin puntaje'}</span>
                  ),
                },
                {
                  label: 'Calificación',
                  value: bureau.rating ?? '—',
                  hint: bureau.thinFile ? 'Poca historia crediticia' : undefined,
                },
                {
                  label: 'Mora vigente',
                  value: (
                    <Badge variant={bureau.currentDelinquencyDays > 0 ? 'danger' : 'success'}>
                      {bureau.currentDelinquencyDays > 0
                        ? `${bureau.currentDelinquencyDays} días`
                        : 'Al día'}
                    </Badge>
                  ),
                },
                { label: 'Consultas (6 meses)', value: bureau.inquiriesLast6Months },
                {
                  label: 'Deuda total',
                  value: <MoneyText value={bureau.totalDebt.amount} size="sm" />,
                },
                {
                  label: 'Cuotas mensuales',
                  value: <MoneyText value={bureau.monthlyPayments.amount} size="sm" />,
                },
              ]}
            />
            {bureau.obligations.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entidad</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">Cuota</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bureau.obligations.map((o) => (
                    <TableRow key={o.entity}>
                      <TableCell className="font-medium">{o.entity}</TableCell>
                      <TableCell>{o.type}</TableCell>
                      <TableCell className="text-right">
                        <MoneyText value={o.balance.amount} size="sm" />
                      </TableCell>
                      <TableCell className="text-right">
                        <MoneyText value={o.monthlyPayment.amount} size="sm" />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            o.status === 'DELINQUENT'
                              ? 'danger'
                              : o.status === 'CURRENT'
                                ? 'success'
                                : 'outline'
                          }
                        >
                          {
                            { CURRENT: 'Al día', DELINQUENT: 'En mora', CLOSED: 'Cerrada' }[
                              o.status
                            ]
                          }
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Aún no se consulta la central (falta el consentimiento).
          </p>
        )}
      </Panel>

      <Panel
        title="Identidad (KYC) y listas"
        description={kyc ? `${kyc.provider} · ${formatDateTime(kyc.checkedAt)}` : undefined}
      >
        {kyc ? (
          <DataList
            columns={4}
            items={[
              {
                label: 'Identidad',
                value: (
                  <Badge
                    variant={
                      kyc.status === 'VERIFIED'
                        ? 'success'
                        : kyc.status === 'FAILED'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {
                      { VERIFIED: 'Verificada', PENDING: 'Pendiente', FAILED: 'Fallida' }[
                        kyc.status
                      ]
                    }
                  </Badge>
                ),
              },
              { label: 'Coincidencia con la cédula', value: kyc.documentMatch ? 'Sí' : 'No' },
              {
                label: 'Prueba de vida',
                value: kyc.livenessScore ? formatRate(kyc.livenessScore, '', 0) : '—',
              },
              {
                label: 'Listas restrictivas',
                value: (
                  <Badge
                    variant={
                      kyc.sanctions === 'CLEAR'
                        ? 'success'
                        : kyc.sanctions === 'MATCH'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {
                      { CLEAR: 'Sin coincidencias', REVIEW: 'Por revisar', MATCH: 'Coincidencia' }[
                        kyc.sanctions
                      ]
                    }
                  </Badge>
                ),
                hint: kyc.listsChecked.join(' · '),
              },
            ]}
          />
        ) : (
          <p className="text-sm text-muted-foreground">KYC pendiente.</p>
        )}
      </Panel>

      {scorecard.length > 0 && (
        <Panel
          title="Scorecard del motor"
          description="Factores y puntos según la política de originación vigente."
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Factor</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead className="text-right">Peso</TableHead>
                <TableHead className="text-right">Puntos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scorecard.map((f) => (
                <TableRow key={f.code}>
                  <TableCell className="font-medium">{f.label}</TableCell>
                  <TableCell>{f.value}</TableCell>
                  <TableCell className="text-right">{formatRate(f.weight, '', 0)}</TableCell>
                  <TableCell className="text-right tabular-nums">{f.points}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
    </div>
  );
}
